import { chartSeriesColors } from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { ErrorComponent } from '@e2m-customer-portal/ui';
import { getTime, Interval, toDate } from 'date-fns';
import { dateAxisFormatter, tooltipPositioner } from 'domains/forecast/feed-in-page/feed-in-chart/utils';
import { ONE_HOUR_MILLISECONDS } from 'domains/forecast/utils';
import { Device } from 'generated/graphql';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useBatteryForecastData } from 'hooks/use-forecast-data/use-forecast-data';
import { equals } from 'ramda';
import { memo, ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTooltipLineChart, getDataAfrrMinus, getDataAfrrPlus, getDataFcr, getPowerFeedData } from './utils';

export interface LineChartBatteryProps {
  device: Device;
  interval: Interval;
  marketLocationName: string;
  isMobile?: boolean;
}

export function LineChartBatteryMemo({
  device,
  interval,
  marketLocationName,
  isMobile,
}: Readonly<LineChartBatteryProps>): ReactElement<LineChartBatteryProps> {
  const { t } = useTranslation();
  const { code: locale } = useLocale();
  const unit = 'kW';
  const [forecasts, error] = useBatteryForecastData([device.id], interval);
  const sortedSlices = (forecasts && [...forecasts[0].slices].sort((a, b) => getTime(a.date) - getTime(b.date))) ?? [];
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const options: Highcharts.Options = {
    chart: {
      height: isMobile ? 300 : 315,
      marginLeft: isMobile ? undefined : 193,
      marginRight: isMobile ? undefined : 31,
      marginTop: isMobile ? 20 : 45,
      marginBottom: isMobile ? undefined : 48,
      scrollablePlotArea: isMobile ? { minWidth: 2000, scrollPositionX: 0 } : undefined,
    },
    legend: {
      useHTML: true,
      enabled: !isMobile,
      layout: 'horizontal',
      align: 'right',
      verticalAlign: 'top',
      x: -10,
      y: -5,
      maxHeight: 50,
      itemStyle: {
        textOverflow: 'visible',
      },
    },

    tooltip: {
      useHTML: true,
      shared: true,
      outside: true,
      style: { zIndex: 1300 },
      formatter: formatTooltipLineChart(t, locale, marketLocationName, device.name, toDate(interval.end).getTime()),
      positioner: tooltipPositioner,
    },
    yAxis: [
      {
        id: 'power_feed',
        gridLineWidth: 1,
        labels: {
          align: 'right',
          format: '{value}',
          style: {
            color: chartSeriesColors.powerFeed,
          },
          x: -10,
        },
        tickAmount: 7,
        max: null,
        min: null,
        title: {
          text: `${t('SHARED_PRODUCT_POWER_FEED_BATTERY')} (${unit})`,
        },
      },
    ],
    xAxis: [
      {
        type: 'datetime',
        max: getTime(interval.end),
        min: getTime(interval.start),
        gridLineWidth: 1,
        gridLineDashStyle: 'Dash',
        startOnTick: true,
        minPadding: 0,
        endOnTick: true,
        maxPadding: 0,
        tickInterval: ONE_HOUR_MILLISECONDS,
        labels: {
          useHTML: true,
          formatter: dateAxisFormatter(t),
        },
      },
    ],
    series: [
      {
        type: 'line',
        id: `production-${device.id}`,
        step: 'left',
        name: t('SHARED_PRODUCT_POWER_FEED_BATTERY'),
        color: chartSeriesColors.powerFeed,
        marker: { enabled: true, radius: 1, symbol: 'circle' },
        data: getPowerFeedData(sortedSlices, toDate(interval.end).getTime()),
        yAxis: 'power_feed',
        zIndex: 2,
      },
      {
        type: 'arearange',
        id: `fcr-${device.id}`,
        step: 'left',
        name: t('SHARED_PRODUCT_FCR'),
        color: chartSeriesColors.fcr,
        marker: { enabled: true, radius: 1, symbol: 'circle' },
        data: getDataFcr(sortedSlices, sortedSlices, toDate(interval.end).getTime(), undefined, true),
        yAxis: 'power_feed',
        zIndex: 3,
      },
      {
        type: 'arearange',
        id: `afrr-minus-${device.id}`,
        step: 'left',
        name: t('SHARED_PRODUCT_AFRR_NEGATIVE_BATTERY'),
        color: chartSeriesColors.afrrMinus,
        marker: { enabled: true, radius: 1, symbol: 'circle' },
        data: getDataAfrrMinus(sortedSlices, sortedSlices, toDate(interval.end).getTime(), undefined, true),
        yAxis: 'power_feed',
        zIndex: 4,
      },
      {
        type: 'arearange',
        id: `afrr-plus-${device.id}`,
        step: 'left',
        name: t('SHARED_PRODUCT_AFRR_POSITIVE_BATTERY'),
        color: chartSeriesColors.afrrPlus,
        marker: { enabled: true, radius: 1, symbol: 'circle' },
        data: getDataAfrrPlus(sortedSlices, sortedSlices, toDate(interval.end).getTime(), undefined, true),
        yAxis: 'power_feed',
        zIndex: 4,
      },
    ],
  };

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return <HighchartsReact options={options} highcharts={Highcharts} ref={chartRef} />;
}

export const LineChartBattery = memo(LineChartBatteryMemo, equals);
